import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { message, Dropdown } from "antd";
import { PlusCircleOutlined, SaveOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import type { MenuProps } from "antd";
import type { Creator } from "@/shared/types/campaign/creator";
import { CreatorTable } from "./CreatorTable";
import { CreatorGrid } from "./CreatorGrid";
import { creatorListAPI, CreatorWithAssignmentResponse } from "@/api/creatorList";
import { extractAvatarUrl } from "@/shared/utils/socialProfile";
import type { CreatorInterestStatus } from "@/shared/types/campaign";
import { blockAPI, BlockData, BlockType } from "@/api/block";
import { createShortUrl, buildShortUrl } from "@/api/shortUrl";
import { Icon } from "@/shared/components/Icon";
import { LoadingWrapper } from "@/shared/components";
import { flattenCreatorsForExport } from "../../utils/creatorExport";

const Page = styled.div`
	padding: 0;
	min-height: 100vh;
`;

const TopBar = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	margin-bottom: 1rem;
	padding: 0 0.75rem;
`;

const Container = styled.div`
	border: 2px dashed #4460ef;
	border-radius: 12px;
	padding: 1rem;
	background: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	cursor: pointer;
	width: 100%;
	transition: border-color 0.2s;

	&:hover {
		border-color: #3651d9;
	}
`;

const UploadDot = styled.div`
	display: grid;
	place-items: center;
`;

const ActionText = styled.span`
	color: #4460ef;
	font-weight: 600;
`;

const ActionButton = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
	color: #333;
	height: 40px;

	svg {
		font-size: 18px;
		color: #4460ef;
	}
`;

const ButtonsRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 0 0.75rem;
	height: 44px;
	position: relative;
`;

const CenterActions = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
`;

const RightActions = styled.div`
	display: flex;
	align-items: center;
	gap: 32px;
	margin-left: auto;
`;

const ViewToggleBox = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 12px;
	background: white;
	border-radius: 6px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	height: 40px;
`;

const ViewModeIcon = styled(Icon)<{ $active: boolean }>`
	opacity: ${(props) => (props.$active ? 1 : 0.6)};
	filter: ${(props) =>
		props.$active
			? "invert(36%) sepia(84%) saturate(2572%) hue-rotate(315deg) brightness(98%) contrast(96%)"
			: "invert(60%)"};
`;

const ViewIcon = styled.div<{ $active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 6px;
	border-radius: 4px;
	transition: all 0.2s;
	background: ${(props) => (props.$active ? "#f0f5ff" : "transparent")};

	svg {
		font-size: 18px;
		color: ${(props) => (props.$active ? "#4460ef" : "#999")};
	}

	&:hover {
		background: #f5f5f5;
	}
`;

const Toggle = styled.div<{ $active: boolean }>`
	width: 32px;
	height: 18px;
	border-radius: 9px;
	background: ${(props) => (props.$active ? "#4460ef" : "#d9d9d9")};
	position: relative;
	cursor: pointer;
	transition: background 0.2s;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		left: ${(props) => (props.$active ? "16px" : "2px")};
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: white;
		transition: left 0.2s;
	}
`;

const ToggleWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	height: 40px;
`;

const ToggleLabel = styled.span`
	color: #666;
	font-size: 14px;
	font-weight: 500;
`;

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: #fafafa;
	border-radius: 8px;
	border: 2px dashed #d9d9d9;
`;

const EmptyIcon = styled.div`
	font-size: 4rem;
	margin-bottom: 1rem;
	opacity: 0.6;
`;

const EmptyTitle = styled.h3`
	font-size: 1.5rem;
	font-weight: 600;
	color: #333;
	margin-bottom: 0.5rem;
`;

const EmptyMessage = styled.p`
	font-size: 1rem;
	color: #666;
	margin: 0;
`;

const IconImg = styled(Icon)`
	width: 18px;
	height: 18px;
	cursor: pointer;
`;

const OrderButton = styled.button<{ $variant?: "save" | "cancel" }>`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	background: ${(props) => (props.$variant === "save" ? "#F72586" : "#4460ef")};
	border: none;
	border-radius: 6px;
	color: white;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
	height: 40px;

	&:hover:not(:disabled) {
		opacity: 0.9;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	svg {
		font-size: 16px;
	}
`;

const PanelOverlay = styled.div<{ $isOpen: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	z-index: 1001;
	opacity: ${(props) => (props.$isOpen ? 1 : 0)};
	visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
	transition:
		opacity 0.3s,
		visibility 0.3s;
`;

const PanelContainer = styled.div<{ $isOpen: boolean }>`
	position: fixed;
	top: 80px;
	right: 0;
	width: 100%;
	height: calc(100vh - 80px);
	background: white;
	z-index: 1002;
	transform: translateX(${(props) => (props.$isOpen ? "0" : "100%")});
	transition: transform 0.3s ease-in-out;
	box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const PanelHeader = styled.div`
	padding: 1.5rem;
	border-bottom: 1px solid #f0f0f0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: white;
`;

const PanelTitle = styled.h2`
	margin: 0;
	font-size: 20px;
	font-weight: 600;
	color: #333;
`;

const CloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 0.5rem;
	border: none;
	background: transparent;
	cursor: pointer;
	border-radius: 6px;
	transition: background 0.2s;
	color: #666;
	font-size: 14px;
	font-weight: 500;

	&:hover {
		background: #f5f5f5;
	}

	svg {
		font-size: 18px;
		color: #4460ef;
	}
`;

const PanelContent = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 1.5rem;
	background: #f5f5f5;
`;

const PanelLoadingWrapper = styled(LoadingWrapper)`
	min-height: 100%;
`;

export interface CreatorDisplayLayoutProps {
	// Page configuration
	pageTitle: string; // e.g., "Creator List" or "Creator Outreach"
	addCreatorText: string; // e.g., "Add/Edit Creators to Creator List"
	mode?: "list" | "grouping"; // 'list' for normal list/outreach, 'grouping' for grouping mode
	// User assignment
	userAssignment?: {
		isAssigned: boolean;
		roles: string[];
	} | null;

	// Campaign and block IDs
	campaignId: string | null;
	blockId: string | null;
	displayId?: string | null;
	outreachId?: string | null;

	displayCreators: Creator[];

	// State
	clientVisible: boolean;
	viewMode: "table" | "grid";
	creatorVisibility: { [key: string]: boolean };
	isCustomizing: boolean;
	displayFields?: Array<{ key: string; label: string }>;

	// Handlers
	onAddCreator: () => void;
	onGetUrl: () => void | Promise<void>;
	onCustomizeOrder: () => void; // For table view "Customize Rows"
	onSaveOrder: () => void;
	onClientVisibleToggle: () => void;
	onCreatorVisibilityToggle: (creatorId: string) => void;
	onSettingsClick: (creatorId: string) => void;
	onCommentClick: (creatorId: string) => void;
	onRemoveFromCampaign?: (assignmentId: string, creatorName?: string) => void;
	onCustomizeColumns?: () => void; // For table view
	onCustomizeDataInGroups?: () => void; // For grouping mode "Customize Data show in Groups"
	onCustomizeOrderButton?: () => void; // For grid view "Customize Order"
	onViewModeChange?: (mode: "table" | "grid") => void; // Optional for grouping mode
	onOrderChange: (creators: Creator[]) => void;
	onInterestChange?: (creatorId: string, interested: CreatorInterestStatus | undefined) => void; // For outreach interest selection

	onCreatorAddedToOutreach?: (creator: Creator) => void; // Callback when creator is added to outreach
	onCreatorRemovedFromOutreach?: (creatorId: string) => void; // Callback when creator is removed from outreach

	canManage?: boolean;

	onCategoryTierChange?: (creatorId: string, field: "categories" | "tier", value: string[] | string) => void;
	onUpdateCreator?: (update: {
		id: string;
		recommendation?: string[];
		name?: string;
		img_url?: string;
		contactEmail?: string | null;
	}) => void;
	finalRateCurrency?: string;
	finalRateConvertedCurrency?: string;

	// Custom content (optional)
	emptyStateTitle?: string;
	emptyStateMessage?: string;

	// For grouping mode - custom render function
	renderGroupingContent?: () => React.ReactNode;

	// Loading state
	isSaving?: boolean;
}

export const CreatorDisplayLayout: React.FC<CreatorDisplayLayoutProps> = ({
	pageTitle,
	addCreatorText,
	displayId: _displayId,
	mode = "list",
	userAssignment,
	campaignId,
	blockId,
	displayCreators,
	clientVisible,
	viewMode,
	creatorVisibility,
	isCustomizing,
	displayFields,
	onAddCreator,
	onGetUrl: _onGetUrl,
	onCustomizeOrder,
	onSaveOrder,
	onClientVisibleToggle,
	onCreatorVisibilityToggle,
	onSettingsClick,
	onCommentClick,
	onCustomizeColumns,
	onCustomizeDataInGroups,
	onCustomizeOrderButton,
	onViewModeChange,
	onOrderChange,
	onInterestChange,
	emptyStateTitle = "Missing Parameters",
	emptyStateMessage = "Campaign ID and Block ID are required to view creators.",
	renderGroupingContent,
	outreachId,
	onCreatorAddedToOutreach,
	onCreatorRemovedFromOutreach,
	onRemoveFromCampaign,
	canManage = false,
	onCategoryTierChange,
	onUpdateCreator,
	finalRateCurrency,
	finalRateConvertedCurrency,
	isSaving = false,
}) => {
	// Check if user is client only - use campaign roles from userAssignment
	const STAFF_ROLES = ["Lead", "Support", "Account Manager"];
	const isClientOnly =
		(userAssignment?.roles ?? []).some((r) => r.toLowerCase() === "client") &&
		!(userAssignment?.roles ?? []).some((r) => STAFF_ROLES.includes(r));
	const handleContactEmailChange = (assignmentId: string, contactEmail: string | null) => {
		const creator = displayCreators.find((item) => (item.assignmentId || item.assignment?.id) === assignmentId);
		if (creator) onUpdateCreator?.({ id: creator.id, contactEmail });
	};

	// Copy link state
	const [copyState, setCopyState] = useState<"idle" | "loading" | "done">("idle");
	const shortLinkRef = useRef<string | null>(null);

	useEffect(() => {
		const path = window.location.pathname + window.location.search;
		createShortUrl(path)
			.then((res) => {
				shortLinkRef.current = buildShortUrl(res.code, path);
			})
			.catch(console.error);
	}, []);

	const handleCopyLink = async () => {
		if (copyState === "loading") return;
		setCopyState("loading");
		const path = window.location.pathname + window.location.search;
		const linkToCopy = shortLinkRef.current || path;

		try {
			await navigator.clipboard.writeText(linkToCopy);
			message.success("Link copied to clipboard!");
			setCopyState("done");
			setTimeout(() => setCopyState("idle"), 2000);
		} catch (err) {
			console.error("Clipboard error:", err);
			message.error("Unable to copy link to clipboard");
			setCopyState("idle");
		}
	};

	// Export state
	const [isExporting, setIsExporting] = useState(false);

	// Export to Excel
	const handleExportExcel = async () => {
		if (isExporting || displayCreators.length === 0) {
			if (displayCreators.length === 0) {
				message.warning("No creators to export");
			}
			return;
		}

		setIsExporting(true);
		try {
			const flattenedData = flattenCreatorsForExport(displayCreators);
			const worksheet = XLSX.utils.json_to_sheet(flattenedData);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Creators");

			// Auto-size columns
			const colWidths = Object.keys(flattenedData[0] || {}).map(() => 15);
			worksheet["!cols"] = colWidths.map((w) => ({ wch: w }));

			const filename = `creators_${new Date().getTime()}.xlsx`;
			XLSX.writeFile(workbook, filename);
			message.success("Creators exported to Excel successfully!");
		} catch (error) {
			console.error("Error exporting to Excel:", error);
			message.error("Failed to export creators to Excel");
		} finally {
			setIsExporting(false);
		}
	};

	// Export to CSV
	const handleExportCSV = async () => {
		if (isExporting || displayCreators.length === 0) {
			if (displayCreators.length === 0) {
				message.warning("No creators to export");
			}
			return;
		}

		setIsExporting(true);
		try {
			const flattenedData = flattenCreatorsForExport(displayCreators);
			const worksheet = XLSX.utils.json_to_sheet(flattenedData);
			const csv = XLSX.utils.sheet_to_csv(worksheet);

			const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
			const filename = `creators_${new Date().getTime()}.csv`;
			saveAs(blob, filename);
			message.success("Creators exported to CSV successfully!");
		} catch (error) {
			console.error("Error exporting to CSV:", error);
			message.error("Failed to export creators to CSV");
		} finally {
			setIsExporting(false);
		}
	};

	const exportMenuItems: MenuProps["items"] = [
		{
			key: "excel",
			label: "Excel (XLSX)",
			onClick: handleExportExcel,
		},
		{
			key: "csv",
			label: "CSV",
			onClick: handleExportCSV,
		},
	];

	// Panel state for Add Creator (only for Outreach)
	const [isAddCreatorPanelOpen, setIsAddCreatorPanelOpen] = useState(false);
	const [panelCreators, setPanelCreators] = useState<Creator[]>([]);
	// Approvals, client comments and recommendations belong to the Creator List block, not the Outreach block.
	const [panelSourceBlockId, setPanelSourceBlockId] = useState<string | null>(null);
	const [isPanelLoading, setIsPanelLoading] = useState(false);

	const formatCreatorsForPanel = (
		items: CreatorWithAssignmentResponse[],
		currentOutreachCreators: Creator[],
	): Creator[] => {
		const inOutreachIds = new Set(currentOutreachCreators.map((c) => c.id));

		return items.map((item) => {
			const creator: Creator = {
				id: item.creator.id,
				name: item.creator.name || "N/A",
				contactEmail: item.creator.contactEmail ?? item.assignment?.contactEmail ?? "",
				inList: inOutreachIds.has(item.creator.id),
				assignment: item.assignment,
				assignmentId: item.assignment?.id,
				addedToCampaign: item.assignment?.addedToCampaign || false,
				creatorInterested: item.assignment?.creatorInterested,
			};

			const avatarUrl = extractAvatarUrl(item.socialAccounts);
			if (avatarUrl) {
				creator.img_url = avatarUrl;
			}

			// Basic fields
			creator.category = (item.creator.categories || []).join(", ");
			creator.creatorTier = item.creator.tier || "";
			creator.sampleContent = (item.creator.sampleContents || []).join(", ");
			creator.rationaleForProposing = item.creator.rationaleForProposing || "";
			creator.location = item.creator.location || "";

			// Social accounts per platform
			Object.entries(item.socialAccounts || {}).forEach(([platform, account]) => {
				const acc = account as any;
				const key = platform.toLowerCase();
				creator[key] = {
					followers: acc.followers,
					avgEngagementRate: acc.avgEngagementRate,
					avgViews: acc.avgViews,
					localAudiencePercentage: acc.localAudiencePercentage,
					femalePercentage: acc.femalePercentage,
					malePercentage: acc.malePercentage,
					audienceAgeDistribution: acc.audienceAgeDistribution,
					platformUsername: acc.platformUsername,
				};
			});

			return creator;
		});
	};

	const loadPanelCreators = async () => {
		if (!campaignId || !blockId) {
			return;
		}
		setIsPanelLoading(true);
		try {
			// Fetch blocks using DFS to find Creator List block
			const blocks = await blockAPI.getByCampaignId(campaignId);

			// Helper functions for DFS
			const findParents = (targetBlockId: string, visited = new Set<string>()): BlockData[] => {
				if (visited.has(targetBlockId)) return [];
				visited.add(targetBlockId);

				const parents = blocks.filter((block) => block.target.includes(targetBlockId));
				const allParents = [...parents];

				parents.forEach((parent) => {
					allParents.push(...findParents(parent.id, visited));
				});

				return allParents;
			};

			const findDescendants = (parentBlockId: string, visited = new Set<string>()): BlockData[] => {
				if (visited.has(parentBlockId)) return [];
				visited.add(parentBlockId);

				const block = blocks.find((b) => b.id === parentBlockId);
				if (!block) return [];

				const descendants: BlockData[] = [block];
				block.target.forEach((targetId) => {
					descendants.push(...findDescendants(targetId, visited));
				});

				return descendants;
			};

			// Get all ancestors (parents recursively)
			const ancestors = findParents(blockId);

			// Find market blocks among ancestors
			const marketBlocks = ancestors.filter((block) => block.type === BlockType.MARKET);

			// For each market, find Creator List in its descendants
			let creatorListBlock: BlockData | null = null;
			for (const market of marketBlocks) {
				const descendants = findDescendants(market.id);
				const creatorList = descendants.find((block) => block.type === BlockType.CREATOR_LIST);
				if (creatorList) {
					creatorListBlock = creatorList;
					break;
				}
			}

			if (!creatorListBlock) {
				console.warn("No Creator List block found for this outreach");
				setPanelCreators([]);
				return;
			}

			setPanelSourceBlockId(creatorListBlock.id);
			const list = await creatorListAPI.getCreatorLists(campaignId, creatorListBlock.id);
			if (!list?.id) {
				setPanelCreators([]);
				return;
			}
			const raw = await creatorListAPI.getCreatorsFromList(campaignId, creatorListBlock.id, list.id);
			const clientVisibleCreators = raw.filter((item) => item.assignment?.clientVisible === true);
			const formatted = formatCreatorsForPanel(clientVisibleCreators, displayCreators);
			setPanelCreators(formatted);
		} catch (error) {
			console.error("Error loading creators for outreach panel:", error);
			setPanelCreators([]);
		} finally {
			setIsPanelLoading(false);
		}
	};

	const handleOpenAddCreatorPanel = () => {
		if (pageTitle === "Creator Outreach") {
			loadPanelCreators();
			setIsAddCreatorPanelOpen(true);
		} else {
			onAddCreator();
		}
	};

	const handleCloseAddCreatorPanel = () => {
		setIsAddCreatorPanelOpen(false);
	};

	const handleAddToList = (creatorId: string) => {
		// Find the creator in panelCreators
		const creator = panelCreators.find((c) => c.id === creatorId);
		if (creator) {
			// Update panel creators: mark as in outreach
			const updatedCreator = {
				...creator,
				inList: true,
				assignment: {
					...creator.assignment,
					id: creator.assignment?.id,
					outreachId: outreachId || undefined,
				},
			};

			setPanelCreators((prevCreators) => prevCreators.map((c) => (c.id === creatorId ? updatedCreator : c)));

			// Notify parent to add to main outreach list
			if (onCreatorAddedToOutreach) {
				onCreatorAddedToOutreach(updatedCreator);
			}
		}
	};

	const handleRemoveFromList = (creatorId: string) => {
		// Update panel creators: mark as not in outreach
		setPanelCreators((prevCreators) =>
			prevCreators.map((c) =>
				c.id === creatorId
					? {
							...c,
							inList: false,
							assignment: {
								...c.assignment,
								outreachId: undefined,
							},
						}
					: c,
			),
		);

		// Notify parent to remove from main outreach list
		if (onCreatorRemovedFromOutreach) {
			onCreatorRemovedFromOutreach(creatorId);
		}
	};

	// Enrich creators with assignment data (campaignId, blockId) if missing
	const enrichCreatorsWithAssignmentData = (creators: Creator[]): Creator[] => {
		return creators.map((creator) => {
			// If assignment exists but missing campaignId/blockId, add them
			if (creator.assignment) {
				return {
					...creator,
					assignment: {
						...creator.assignment,
						campaignId: creator.assignment.campaignId || campaignId || undefined,
						blockId: creator.assignment.blockId || blockId || undefined,
					},
					assignmentId: creator.assignmentId || creator.assignment.id,
					campaignId: creator.campaignId || campaignId || undefined,
					blockId: creator.blockId || blockId || undefined,
				};
			}
			// If no assignment but we have campaignId/blockId, create a minimal assignment
			if (campaignId && blockId && creator.assignmentId) {
				return {
					...creator,
					assignment: {
						id: creator.assignmentId,
						campaignId,
						blockId,
					},
					campaignId,
					blockId,
				};
			}
			return creator;
		});
	};

	// Show error message if required params are missing
	if (!campaignId || !blockId) {
		return (
			<Page>
				<EmptyState>
					<EmptyIcon>⚠️</EmptyIcon>
					<EmptyTitle>{emptyStateTitle}</EmptyTitle>
					<EmptyMessage>{emptyStateMessage}</EmptyMessage>
				</EmptyState>
			</Page>
		);
	}

	const effectiveDisplayFields = displayFields && displayFields.length > 0 ? displayFields : undefined;

	return (
		<Page>
			{canManage && (
				<TopBar>
					<Container onClick={handleOpenAddCreatorPanel}>
						<UploadDot>
							<PlusCircleOutlined style={{ color: "#4460ef", fontSize: 24 }} />
						</UploadDot>
						<ActionText>{addCreatorText}</ActionText>
					</Container>
				</TopBar>
			)}

			{canManage && (
				<ButtonsRow>
					{isCustomizing && (
						<CenterActions>
							<OrderButton $variant="save" onClick={onSaveOrder} disabled={isSaving}>
								<SaveOutlined />
								{isSaving ? "Saving..." : "Save Changes"}
							</OrderButton>
							<OrderButton
								$variant="cancel"
								onClick={mode === "grouping" ? onCustomizeOrderButton : onCustomizeOrder}
								disabled={isSaving}
							>
								<CloseOutlined />
								Cancel
							</OrderButton>
						</CenterActions>
					)}

					{!isCustomizing && (
						<RightActions>
							<ActionButton
								onClick={handleCopyLink}
								style={{
									color: copyState === "done" ? "#52c41a" : copyState === "loading" ? "#999" : "#333",
								}}
							>
								<IconImg
									name="copy"
									size={18}
									alt="Copy"
									preserveOriginalColor
									style={{ opacity: copyState === "loading" ? 0.5 : 1 }}
								/>
								{copyState === "loading"
									? "Copying..."
									: copyState === "done"
										? "Copied!"
										: "Copy Link"}
							</ActionButton>
							<Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
								<ActionButton style={{ color: isExporting ? "#999" : "#333" }}>
									<DownloadOutlined
										style={{ fontSize: 24, color: "#4460ef", opacity: isExporting ? 0.5 : 1 }}
									/>
									{isExporting ? "Exporting..." : "Export"}
								</ActionButton>
							</Dropdown>
							{mode === "grouping" ? (
								<>
									<ActionButton onClick={onCustomizeDataInGroups}>
										<IconImg
											name="columnSetting"
											size={18}
											alt="Customize Data"
											preserveOriginalColor
										/>
										Customize Data
									</ActionButton>
									<ActionButton onClick={onCustomizeOrderButton}>
										<IconImg name="moving" size={18} alt="Customize Order" preserveOriginalColor />
										Customize Order
									</ActionButton>
								</>
							) : (
								<>
									{viewMode === "table" && (
										<>
											<ActionButton onClick={onCustomizeColumns}>
												<IconImg
													name="columnSetting"
													size={18}
													alt="Customize Columns"
													preserveOriginalColor
												/>
												Customize Columns
											</ActionButton>
											<ActionButton onClick={onCustomizeOrder}>
												<IconImg
													name="moving"
													size={18}
													alt="Customize"
													preserveOriginalColor
												/>
												Customize Rows
											</ActionButton>
										</>
									)}
									{viewMode === "grid" && (
										<ActionButton onClick={onCustomizeOrderButton}>
											<IconImg
												name="moving"
												size={18}
												alt="Customize Order"
												preserveOriginalColor
											/>
											Customize Order
										</ActionButton>
									)}
								</>
							)}
							{mode === "list" && (
								<ToggleWrapper>
									<Toggle $active={clientVisible} onClick={onClientVisibleToggle} />
									<ToggleLabel>Visible to Client</ToggleLabel>
								</ToggleWrapper>
							)}
							{mode !== "grouping" && onViewModeChange && (
								<ViewToggleBox>
									<ViewIcon $active={viewMode === "table"} onClick={() => onViewModeChange("table")}>
										<ViewModeIcon
											name="tableView"
											size={18}
											alt="Table View"
											$active={viewMode === "table"}
										/>
									</ViewIcon>
									<ViewIcon $active={viewMode === "grid"} onClick={() => onViewModeChange("grid")}>
										<ViewModeIcon
											name="gridView"
											size={18}
											alt="Grid View"
											$active={viewMode === "grid"}
										/>
									</ViewIcon>
								</ViewToggleBox>
							)}
						</RightActions>
					)}
				</ButtonsRow>
			)}

			{mode === "grouping" && renderGroupingContent ? (
				renderGroupingContent()
			) : isClientOnly ? (
				<CreatorGrid
					creators={enrichCreatorsWithAssignmentData(displayCreators)}
					isCustomizing={false}
					creatorVisibility={creatorVisibility}
					onCreatorVisibilityToggle={() => {}}
					onCreatorReorder={() => {}}
					onCommentClick={onCommentClick}
					onSettingsClick={onSettingsClick}
					isClientOnly={true}
					isOutreach={pageTitle === "Creator Outreach"}
					campaignId={campaignId || undefined}
					blockId={blockId || undefined}
					finalRateCurrency={finalRateCurrency}
					finalRateConvertedCurrency={finalRateConvertedCurrency}
					onUpdateCreator={onUpdateCreator}
					onContactEmailChange={handleContactEmailChange}
				/>
			) : (
				<>
					{viewMode === "table" && (
						<CreatorTable
							creators={displayCreators}
							isCustomizing={isCustomizing}
							creatorVisibility={creatorVisibility}
							onCreatorVisibilityToggle={onCreatorVisibilityToggle}
							onSettingsClick={onSettingsClick}
							onOrderChange={onOrderChange}
							displayFields={effectiveDisplayFields}
							isClientOnly={isClientOnly}
							isOutreach={pageTitle === "Creator Outreach"}
							onRemoveCreator={onRemoveFromCampaign}
							campaignId={campaignId || undefined}
							blockId={blockId || undefined}
							finalRateCurrency={finalRateCurrency}
							finalRateConvertedCurrency={finalRateConvertedCurrency}
							canManage={canManage}
							onCategoryTierChange={onCategoryTierChange}
							onUpdateCreator={onUpdateCreator}
							onInterestChange={onInterestChange}
							onContactEmailChange={handleContactEmailChange}
						/>
					)}

					{viewMode === "grid" && (
						<CreatorGrid
							creators={displayCreators}
							isCustomizing={isCustomizing}
							creatorVisibility={creatorVisibility}
							onCreatorVisibilityToggle={onCreatorVisibilityToggle}
							onCreatorReorder={onOrderChange}
							onCommentClick={onCommentClick}
							onSettingsClick={onSettingsClick}
							isClientOnly={false}
							isOutreach={pageTitle === "Creator Outreach"}
							canManage={canManage}
							onInterestChange={onInterestChange}
							campaignId={campaignId || undefined}
							blockId={blockId || undefined}
							outreachId={outreachId || undefined}
							finalRateCurrency={finalRateCurrency}
							finalRateConvertedCurrency={finalRateConvertedCurrency}
							onRemoveFromCampaign={onRemoveFromCampaign}
							onCategoryTierChange={onCategoryTierChange}
							onUpdateCreator={onUpdateCreator}
							onContactEmailChange={handleContactEmailChange}
						/>
					)}
				</>
			)}

			{/* {pageTitle === 'Creator Outreach' && (
        <FloatingAddButton onClick={handleOpenAddCreatorPanel}>
          <PlusOutlined />
          Add New Creator
        </FloatingAddButton>
      )} */}

			{/* Add Creator Panel - Only for Outreach */}
			{pageTitle === "Creator Outreach" && (
				<>
					<PanelOverlay $isOpen={isAddCreatorPanelOpen} onClick={handleCloseAddCreatorPanel} />
					<PanelContainer $isOpen={isAddCreatorPanelOpen}>
						<PanelHeader>
							<PanelTitle>Add Creators to Outreach</PanelTitle>
							<CloseButton onClick={handleCloseAddCreatorPanel}>
								<CloseOutlined />
								<span>Close</span>
							</CloseButton>
						</PanelHeader>
						<PanelContent>
							<PanelLoadingWrapper
								isLoading={isPanelLoading}
								label="Loading creators for outreach"
								indicatorSize={80}
								borderRadius={12}
							>
								<CreatorGrid
									creators={panelCreators}
									isCustomizing={false}
									creatorVisibility={panelCreators.reduce(
										(acc, creator) => ({ ...acc, [creator.id]: true }),
										{},
									)}
									onCreatorVisibilityToggle={() => {}}
									onCreatorReorder={() => {}}
									campaignId={campaignId || undefined}
									blockId={panelSourceBlockId || blockId || undefined}
									outreachId={outreachId || undefined}
									canManage={canManage}
									onCommentClick={() => {}}
									onSettingsClick={() => {}}
									isClientOnly={false}
									isOutreach={false}
									isInPanel={true}
									onAddToList={handleAddToList}
									onRemoveFromList={handleRemoveFromList}
									finalRateCurrency={finalRateCurrency}
									finalRateConvertedCurrency={finalRateConvertedCurrency}
								/>
							</PanelLoadingWrapper>
						</PanelContent>
					</PanelContainer>
				</>
			)}
		</Page>
	);
};
